import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import { checkPromoCode } from '../../../../axios/promo_axios';
import { addToCart, addToCartWithPromo, selectCartItems, selectPromoItems } from '../../../../redux/slices/cartSlice';

const ItemPriceBlockV1 = ({ block, itemData }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const cartItems = useSelector(selectCartItems);
  const promoItems = useSelector(selectPromoItems);
  const [selectedVariantIndex, setSelectedVariantIndex] = React.useState(0);
  const [quantity, setQuantity] = React.useState(1);
  const [isQuantityFocused, setIsQuantityFocused] = React.useState(false);
  const [promoCode, setPromoCode] = React.useState('');
  const [isPromoLoading, setIsPromoLoading] = React.useState(false);
  const [promoMessage, setPromoMessage] = React.useState('');
  const [discountPercent, setDiscountPercent] = React.useState(0);

  // console.log('ItemPriceBlockV1 block:', block);

  // Функция для подсчета количества товара в корзине
  const getCartItemQuantity = React.useMemo(() => {
    const itemId = itemData?.id || itemData?.eng_name || 'unknown';
    
    // Подсчитываем количество в обычных товарах
    const regularQuantity = cartItems
      .filter((item) => item.itemId === itemId && item.variantIndex === selectedVariantIndex)
      .reduce((sum, item) => sum + item.quantity, 0);
    
    // Подсчитываем количество в товарах с промокодом
    const promoQuantity = promoItems
      .filter((item) => item.itemId === itemId && item.variantIndex === selectedVariantIndex)
      .reduce((sum, item) => sum + item.quantity, 0);
    
    return regularQuantity + promoQuantity;
  }, [cartItems, promoItems, itemData, selectedVariantIndex]);

  // Проверяем, есть ли товары с промокодом в корзине
  const hasPromoItemsInCart = promoItems.length > 0;

  // Сбрасываем промокод при изменении количества (если количество != 1)
  React.useEffect(() => {
    if (quantity !== 1 && quantity !== '') {
      setPromoCode('');
      setPromoMessage('');
      setDiscountPercent(0);
    }
  }, [quantity]);

  if (!block) {
    return null;
  }

  const formatPrice = (value) => {
    if (!value) {
      return null;
    }

    const normalized = value.replace(/[^0-9,\.\-]/g, '').replace(',', '.');
    const numeric = Number(normalized);

    if (Number.isNaN(numeric)) {
      return null;
    }

    return numeric;
  };

  const formatPriceDisplay = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return null;
    }

    return `${value.toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ₽`;
  };

  let price = null;
  let variantName = null;
  let variants = [];
  let addPriceValues = [];
  let hasPromo = false;

  if (Array.isArray(block.block_content)) {
    block.block_content.forEach((contentItem) => {
      if (!contentItem || !(contentItem.cotent_tag || contentItem.content_tag) || !contentItem.content_text) {
        return;
      }

      const tag = (contentItem.cotent_tag || contentItem.content_tag).toLowerCase();
      const text = contentItem.content_text.trim();

      if (tag === 'price') {
        const parsed = formatPrice(text);
        if (parsed !== null) {
          price = parsed;
        }
      }

      if (tag === 'variant_name') {
        variantName = text;
      }

      if (tag === 'variant') {
        variants = text
          .split(/[;,]/)
          .map((item) => item.trim())
          .filter(Boolean);
      }

      if (tag === 'add_price_val') {
        addPriceValues = text
          .split(/[;,]/)
          .map((item) => formatPrice(item.trim()))
          .filter((value) => value !== null);
      }

      if (tag === 'promo' && text.toLowerCase() !== 'none') {
        hasPromo = true;
      }
    });
  }

  const getVariantAdd = (index) => {
    const addValue = addPriceValues[index];
    if (addValue === undefined || addValue === null || Number.isNaN(addValue)) {
      return 0;
    }
    return addValue;
  };

  const getVariantTotal = (index) => {
    const add = getVariantAdd(index);

    if (price !== null) {
      return price + add;
    }

    if (add !== 0) {
      return add;
    }

    return null;
  };

  const getDisplayedPrice = () => {
    const total = getVariantTotal(selectedVariantIndex);

    if (total === null) {
      return '—';
    }

    const numericQuantity = quantity === '' ? 0 : quantity;
    let finalPrice = total * numericQuantity;

    // Применяем скидку, если она есть
    if (discountPercent > 0) {
      finalPrice = finalPrice * (1 - discountPercent / 100);
    }

    return formatPriceDisplay(finalPrice);
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      return;
    }

    setIsPromoLoading(true);
    setPromoMessage('');

    try {
      const response = await checkPromoCode(promoCode.trim());
      // console.log('Promo code response:', response);

      // Разделяем ответ по символу "*"
      const parts = String(response).split('*');

      if (parts[0] === 'good') {
        // Промо-код принят, получаем процент скидки
        const discount = parseFloat(parts[1]);

        if (!isNaN(discount) && discount > 0) {
          setDiscountPercent(discount);
          setPromoMessage(`Промо-код применен! Скидка ${discount}%`);
        } else {
          setDiscountPercent(0);
          setPromoMessage('Неверный промо-код');
        }
      } else if (response === 'err_used' || parts[0] === 'err_used') {
        // Промо-код уже использован
        setDiscountPercent(0);
        setPromoMessage('Код уже использован');
      } else {
        // Промо-код не принят
        setDiscountPercent(0);
        setPromoMessage('Неверный промо-код');
      }
    } catch (error) {
      // console.error('Error applying promo code:', error);
      setDiscountPercent(0);
      setPromoMessage('Ошибка при применении промо-кода');
    } finally {
      setIsPromoLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!quantity || quantity < 1) return;

    // Получаем данные товара
    const itemId = itemData?.id || itemData?.eng_name || 'unknown';
    const itemName = itemData?.show_name || itemData?.block_title || 'Товар';

    // Получаем первое изображение товара из блока с индексом 0 (как в карточке)
    let itemImage = null;
    let itemPath = '';
    if (itemData?.path && itemData?.eng_name) {
      itemPath = `http://${itemData.path}${itemData.eng_name}/`;
    }

    // console.log('=== DEBUG: itemData ===', itemData);

    if (itemData?.content) {
      try {
        const contentArray = JSON.parse(itemData.content);
        // console.log('=== DEBUG: contentArray ===', contentArray);

        // Берем блок с индексом 0 (как в ItemCardV1)
        if (Array.isArray(contentArray) && contentArray[0]) {
          const firstBlock = contentArray[0];
          // console.log('=== DEBUG: firstBlock ===', firstBlock);
          // console.log('=== DEBUG: firstBlock.block_content ===', firstBlock.block_content);

          if (Array.isArray(firstBlock.block_content)) {
            // Ищем первое изображение с img_name
            // console.log('=== DEBUG: Searching for img in block_content...');
            // firstBlock.block_content.forEach((item, idx) => {
            //   console.log(`=== DEBUG: block_content[${idx}] ===`, item);
            //   console.log(
            //     `=== DEBUG: cotent_type: ${item.cotent_type}, img_name: ${item.img_name}`,
            //   );
            // });

            const imgContent = firstBlock.block_content.find(
              (item) => (item.cotent_type === 'img' || item.content_type === 'img') && item.img_name,
            );

            // console.log('=== DEBUG: imgContent (found) ===', imgContent);

            if (imgContent && imgContent.img_name && imgContent.img_name !== 'none') {
              // Очищаем путь от обратных слешей (как в ItemCardV1)
              const processedImgName = imgContent.img_name.includes('\\')
                ? imgContent.img_name.substring(imgContent.img_name.lastIndexOf('\\') + 1)
                : imgContent.img_name;

              itemImage = itemPath + processedImgName;
              // console.log('=== DEBUG: Found image ===', itemImage);
              // console.log('=== DEBUG: processedImgName ===', processedImgName);
            } else {
              // console.log('=== DEBUG: No valid img_name found ===');
            }
          }
        }
      } catch (error) {
        // console.error('Error parsing item content:', error);
      }
    }

    // console.log('=== DEBUG: Final itemImage ===', itemImage);
    // console.log('=== DEBUG: Final itemPath ===', itemPath);

    const variant = variants[selectedVariantIndex] || 'Стандарт';
    const totalPrice = getVariantTotal(selectedVariantIndex);

    if (totalPrice === null) return;

    // Если есть активный промокод - добавляем с промокодом
    if (discountPercent > 0 && promoCode.trim()) {
      // Проверяем, нет ли уже товара с промокодом в корзине
      if (hasPromoItemsInCart) {
        alert('В корзине может быть только один товар с промокодом. Сначала удалите товар с промокодом из корзины.');
        return;
      }

      dispatch(
        addToCartWithPromo({
          itemId,
          itemName,
          itemImage,
          itemPath,
          variant,
          variantName,
          variantIndex: selectedVariantIndex,
          price: totalPrice,
          quantity,
          promoCode: promoCode.trim(),
          discountPercent,
        }),
      );

      // Сбрасываем промокод после добавления
      setPromoCode('');
      setPromoMessage('');
      setDiscountPercent(0);
      setQuantity(1);
    } else {
      // Добавляем без промокода
      dispatch(
        addToCart({
          itemId,
          itemName,
          itemImage,
          itemPath,
          variant,
          variantName,
          variantIndex: selectedVariantIndex,
          price: totalPrice,
          quantity,
        }),
      );

      setQuantity(1);
    }
  };

  const renderVariants = () => {
    // Если имя варианта "none", не рендерим варианты
    if (variantName && variantName.toLowerCase().trim() === 'none') {
      return null;
    }

    if (!variants.length) {
      return null;
    }

    return (
      <div className={styles.variantsContainer}>
        <div className={styles.variantList}>
          {variantName && <p className={styles.variantName}>{variantName}</p>}
          <ul>
            {variants.map((variant, index) => (
              <li
                key={index}
                className={`${styles.variantItem} ${
                  index === selectedVariantIndex ? styles.activeVariant : ''
                }`}
                onClick={() => setSelectedVariantIndex(index)}
                role='button'
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setSelectedVariantIndex(index);
                  }
                }}>
                <span>{variant}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <section className={styles.itemPriceBlockV1}>
      {block.block_title && <h3 className={styles.title}>{block.block_title}</h3>}

      <div className={styles.contentWrapper}>
        {renderVariants()}

        <div className={styles.bottomSection}>
          <div className={styles.quantityRow}>
            <span className={styles.label}>Количество</span>
            <div className={styles.quantityControls}>
              <button
                type='button'
                className={styles.quantityButton}
                onClick={() => setQuantity((prev) => (prev > 1 ? prev - 1 : prev))}
                aria-label='Decrease quantity'>
                −
              </button>
              <input
                type='number'
                min={1}
                className={styles.quantityInput}
                value={quantity}
                onFocus={() => setIsQuantityFocused(true)}
                onBlur={(event) => {
                  setIsQuantityFocused(false);
                  const value = Number(event.target.value);

                  if (Number.isNaN(value) || value < 1) {
                    setQuantity(1);
                  } else {
                    setQuantity(Math.floor(value));
                  }
                }}
                onChange={(event) => {
                  if (event.target.value === '') {
                    setQuantity('');
                    return;
                  }

                  const value = Number(event.target.value);
                  if (Number.isNaN(value) || value < 1) {
                    return;
                  }

                  setQuantity(Math.floor(value));
                }}
              />
              <button
                type='button'
                className={styles.quantityButton}
                onClick={() => setQuantity((prev) => prev + 1)}
                aria-label='Increase quantity'>
                +
              </button>
            </div>
          </div>

          {hasPromo && quantity === 1 && !hasPromoItemsInCart && (
            <div className={styles.promoContainer}>
              <label className={styles.promoLabel} htmlFor='promoCode'>
                Введите промо код:
              </label>
              <div className={styles.promoInputGroup}>
                <input
                  id='promoCode'
                  type='text'
                  className={styles.promoInput}
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoMessage('');
                    setDiscountPercent(0);
                  }}
                  placeholder='Промо код'
                  disabled={isPromoLoading}
                />
                <button
                  type='button'
                  className={styles.promoButton}
                  onClick={handleApplyPromoCode}
                  disabled={!promoCode.trim() || isPromoLoading}>
                  {isPromoLoading ? 'Проверка...' : 'Применить'}
                </button>
              </div>
              {promoMessage && (
                <div
                  className={`${styles.promoMessage} ${
                    discountPercent > 0 ? styles.promoSuccess : styles.promoError
                  }`}>
                  {promoMessage}
                </div>
              )}
            </div>
          )}

          {hasPromo && quantity === 1 && hasPromoItemsInCart && (
            <div className={styles.promoInfoMessage}>
              Промокод уже активирован
            </div>
          )}

          <div className={styles.priceRow}>
            <span className={styles.label}>Цена</span>
            <span className={styles.value}>{getDisplayedPrice()}</span>
          </div>

          <div className={styles.addToCartContainer}>
            {getCartItemQuantity > 0 && (
              <div className={styles.cartCounterWrapper}>
                <span className={styles.cartCounter}>
                  В корзине: {getCartItemQuantity} шт.
                </span>
                <button
                  type='button'
                  className={styles.goToCartButton}
                  onClick={() => navigate('/cart')}>
                  к корзине
                </button>
              </div>
            )}
            <button
              type='button'
              className={styles.addToCartButton}
              onClick={handleAddToCart}
              disabled={!quantity || quantity < 1}>
              Добавить в корзину
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ItemPriceBlockV1;
